import { useEffect, useState } from 'react';

// Below this zoom level we treat a shrunken visual viewport as a keyboard. Pinch
// zoom shrinks it too, and this app deliberately allows pinch zoom, so without
// this a zoomed-in user would get a phantom keyboard inset.
const MAX_SCALE_FOR_KEYBOARD = 1.05;

/**
 * How many pixels of the layout viewport the on-screen keyboard currently covers.
 *
 * Fixed overlays (`position: fixed`) are laid out against the layout viewport, and
 * the platforms disagree about what the keyboard does to it: some shrink it, and
 * some leave it alone and shrink only the visual viewport. This subtraction is
 * correct either way — it yields ~0 in the first case, where the overlay's own
 * `bottom` already resolves above the keyboard, and the keyboard's height in the
 * second, where it does not. So callers never have to sniff the platform.
 *
 * Returns 0 whenever `visualViewport` is unavailable or `enabled` is false, which
 * leaves layout exactly as it was before this hook existed.
 */
export function useKeyboardInset(enabled: boolean): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!enabled || !vv) {
      setInset(0);
      return;
    }

    const update = () => {
      const covered = window.innerHeight - vv.height - vv.offsetTop;
      setInset(vv.scale > MAX_SCALE_FOR_KEYBOARD ? 0 : Math.max(0, covered));
    };

    update();
    // `scroll` matters as well as `resize`: panning a zoomed or keyboard-shifted
    // page moves offsetTop without changing height.
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [enabled]);

  return inset;
}
